import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Input,
  Form,
  Space,
  message,
  Spin,
  notification,
  Select,
  Tag,
} from "antd";
import { FaPlus, FaEdit, FaSearch } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";

const { Option } = Select;

const FabricManagement = () => {
  const [fabrics, setFabrics] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFabric, setEditingFabric] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchFabrics();
  }, []);

  // ✅ Fetch all fabrics (with status)
  const fetchFabrics = async () => {
    setLoading(true);
    try {
      const response = await apiHandle.get("admin/get-fabrics");
      setFabrics(response?.data?.data || []);
    } catch (error) {
      message.error("Failed to fetch fabrics.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Add or Update fabrics (with status)
  const handleAddOrUpdateFabrics = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingFabric) {
        await apiHandle.post(`admin/edit-fabric/${editingFabric._id}`, {
          name: values.name,
          status: values.status,
        });
        notification.success({ message: "fabric updated successfully" });
      } else {
        await apiHandle.post("admin/add-fabric", {
          name: values.name,
          status: values.status || "Active", // Default status to Active
        });
        message.success("Fabric added successfully");
      }

      fetchFabrics();
      form.resetFields();
      setIsModalVisible(false);
      setEditingFabric(null);
    } catch (error) {
      notification.error({
        message: "Please fill in all required fields correctly.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Edit Fabrics
  const handleEdit = (record) => {
    setEditingFabric(record);
    form.setFieldsValue({
      name: record.name,
      status: record.status || "Active",
    });
    setIsModalVisible(true);
  };

  // ✅ Filter Fabric for Search
  const filteredFabrics = fabrics.filter((fabric) =>
    fabric.name.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Table Columns (With Status)
  const columns = [
    {
      title: "Fabric Name",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space>
          <Button icon={<FaEdit />} onClick={() => handleEdit(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-center mb-4">Fabric Management</h1>

      {/* ✅ Search Bar */}
      <div className="flex justify-between my-4">
        <Input
          placeholder="Search by Fabric name"
          prefix={<FaSearch />}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "300px" }}
        />
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({ status: "Active" }); // Default status
            setIsModalVisible(true);
          }}
        >
          Add Fabric
        </Button>
      </div>

      {/* ✅ Table with Sizes & Status */}
      {loading ? (
        <Spin size="large" className="flex justify-center" />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredFabrics}
          pagination={{ pageSize: 15 }}
          rowKey="_id"
        />
      )}

      {/* ✅ Modal for Add & Edit */}
      <Modal
        title={editingFabric ? "Edit Fabric" : "Add Fabric"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingFabric(null);
          form.resetFields();
        }}
        onOk={handleAddOrUpdateFabrics}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Fabric Name"
            rules={[{ required: true, message: "Please enter a fabric name!" }]}
          >
            <Input />
          </Form.Item>

          {/* ✅ Status Selection */}
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status!" }]}
          >
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FabricManagement;
